import React from 'react'

import classNames from 'classnames'

import { useObject } from 'data/hooks/objects'
import ActionEditor from 'features/actions/ActionEditor'
import { ActionsContextProvider } from 'features/actions/ActionsContextProvider'
import { DeleteActionConfirmModal } from 'features/actions/DeleteActionConfirmModal'

import {
    Dropdown,
    DropdownButton,
    DropdownContent,
    DropdownEndIndicatorIcon,
    DropdownFooterItem,
    DropdownHeadSearch,
    DropdownItem,
    DropdownSub,
    DropdownSubContent,
    DropdownSubTrigger,
} from 'ui/components/Dropdown'
import { ShowOnItemActiveStyle, ShowOnItemHoverStyle } from 'ui/components/Menu/Menu.css'
import { Body } from 'ui/components/Text'

import { useToolbarActionsActionsState } from './hooks/useToolbarActionsActionsState'
import { useToolbarContext } from './hooks/useToolbarContext'

type ToolbarActionsActionsProps = {}

const ToolbarActionsActionsInner: React.FC<ToolbarActionsActionsProps> = ({}) => {
    const {
        isEmpty,
        isLoading,
        isError,
        actions,
        allowAddNewAction,
        allowEditAction,
        allowDeleteAction,
        allowDuplicateAction,
        allowModifyAction,
        editingAction,
        onCommitActionEdit,
        onAddNewAction,
        onEditAction,
        onDuplicateAction,
        onDeleteAction,
        modifyActionMenuSid,
        onModifyActionMenuOpenChange,
        onModifyActionClick,
        actionToDelete,
        onDeleteActionModalOpenChange,
    } = useToolbarActionsActionsState()

    return (
        <>
            <Dropdown modal={false}>
                <DropdownButton
                    size="xs"
                    startIcon={{ name: 'Zap' }}
                    isLoading={isLoading}
                    variant={isError ? 'critical' : 'ghost'}
                    endIcon={isError ? { name: 'AlertCircle' } : undefined}
                >
                    Actions
                </DropdownButton>
                <DropdownContent
                    align="end"
                    side="bottom"
                    head={<DropdownHeadSearch />}
                    style={{
                        width: '300px',
                    }}
                    footer={
                        allowAddNewAction && (
                            <DropdownFooterItem
                                variant="action"
                                label="Add new button"
                                startIcon={{ name: 'Plus' }}
                                showSeparator={true}
                                onClick={onAddNewAction}
                            />
                        )
                    }
                >
                    {isError && (
                        <DropdownItem
                            label={
                                <Body color="textError">There was a problem displaying views</Body>
                            }
                            disabled
                        />
                    )}
                    {isEmpty && (
                        <DropdownItem
                            label={
                                <Body size={{ mobile: 's', tablet: 'm' }} color="textHelper">
                                    No actions to show
                                </Body>
                            }
                            disabled
                        />
                    )}
                    {!isError &&
                        actions.map((action) => (
                            <DropdownSub
                                key={action._sid}
                                modal={false}
                                open={modifyActionMenuSid === action._sid}
                                onOpenChange={(open: boolean) =>
                                    onModifyActionMenuOpenChange(action._sid, open)
                                }
                            >
                                <DropdownSubTrigger
                                    startIcon={{ name: 'Zap' }}
                                    label={action.name}
                                    onClick={
                                        allowModifyAction
                                            ? () => onModifyActionClick(action._sid)
                                            : undefined
                                    }
                                    rightSlotContent={
                                        allowModifyAction && (
                                            <DropdownEndIndicatorIcon
                                                name="MoreHorizontal"
                                                className={classNames(
                                                    ShowOnItemHoverStyle,
                                                    ShowOnItemActiveStyle
                                                )}
                                            />
                                        )
                                    }
                                />
                                <DropdownSubContent style={{ minWidth: '180px', width: '180px' }}>
                                    {allowEditAction && (
                                        <DropdownItem
                                            startIcon={{ name: 'PencilLine' }}
                                            label="Edit"
                                            onClick={() => onEditAction(action)}
                                        />
                                    )}
                                    {allowDuplicateAction && (
                                        <DropdownItem
                                            startIcon={{ name: 'Copy' }}
                                            label="Duplicate"
                                            onClick={() => onDuplicateAction(action)}
                                        />
                                    )}
                                    {allowDeleteAction && (
                                        <DropdownItem
                                            startIcon={{ name: 'Trash2' }}
                                            label="Delete"
                                            variant="destructive"
                                            onClick={() => onDeleteAction(action)}
                                        />
                                    )}
                                </DropdownSubContent>
                            </DropdownSub>
                        ))}
                </DropdownContent>
            </Dropdown>
            {editingAction && (
                <ActionEditor item={editingAction} onRequestClose={onCommitActionEdit} />
            )}
            {actionToDelete && (
                <DeleteActionConfirmModal
                    action={actionToDelete}
                    isOpen={!!actionToDelete}
                    onOpenChange={onDeleteActionModalOpenChange}
                />
            )}
        </>
    )
}

export const ToolbarActionsActions: React.FC = () => {
    const { objectSid } = useToolbarContext()
    const { object } = useObject(objectSid)

    if (!object) return null

    return (
        <ActionsContextProvider object={object}>
            <ToolbarActionsActionsInner />
        </ActionsContextProvider>
    )
}
